import { createSelector } from 'reselect';
import { RootState } from '../../../../types';
import { EntityTypes } from '../../../../constants/entities';
import { IActionDAO } from 'types/domain/action';
import { IEntityReducer } from '../../../reducers/entities';
import { Handles } from 'constants/handles';
import { parseAction, parseActionDAO } from './action';
import { selectFeed } from '../feed';
import { ActionHistory, ActionHistoryItem, selectActionHistory } from './actionHistory';
import { IModuleAssign, IModuleOut, ModuleType } from 'types/domain/module';
import { IRegisterReducer } from 'core/slices/register';
import { IQueueItem } from 'core/slices/queue';
import { getFirstAction } from './actionFirst';
import { getEntities } from 'core/selectors/utils/getEntities';
import { IStateReducer } from 'core/reducers/state';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { Mode } from 'core/slices/session';

export interface IActionFeed {
  feed: IActionFeedItem[];
  index: number;
  next: boolean;
}

export interface IActionFeedItem {
  actionId?: number;
  sessionAttemptId?: number;
  courseProgressId?: number;
  actionRegisterId: string;
  verificationRequestId?: number;
  courseNodeId?: number;
  moduleId?: number;
  visible?: boolean;
}

const entity = (state: RootState) => state.entity.present;
const state = (state: RootState) => state.state;
const feed = selectFeed;
const actionHistory = selectActionHistory;
const register = (state: RootState) => state.register;
const mode = selectSessionMode;

export const selectActionFeed = createSelector([entity, state, feed, actionHistory, register, mode], parseActionFeed);

export function parseActionFeed(
  entity: IEntityReducer,
  state: IStateReducer,
  queue: IQueueItem[],
  history: ActionHistory,
  register: IRegisterReducer,
  mode: Mode,
): IActionFeed {
  const previewHist =
    mode === Mode.ADMIN && state[EntityTypes.ACTION]
      ? [{ actionId: state[EntityTypes.ACTION], actionRegisterId: null }]
      : [];

  console.log(queue);

  const feed = queue.flatMap(item => {
    if (item.moduleId) {
      return nextModule(item.sessionAttemptId, entity, item.moduleId, history[item.sessionAttemptId] ?? previewHist);
    } else return [{ courseNodeId: item.courseNodeId, actionRegisterId: null }];
  });

  return {
    feed,
    index: register.index,
    next: register.isNext,
  } as IActionFeed;
}

function processModOut(entity, modOut: IModuleOut) {
  if (!modOut) return null;

  return parseActionDAO(entity, modOut.targetId);
}

function addActionToFuture(
  sessionAttemptId: number,
  entity: IEntityReducer,
  action: IActionDAO,
  future: IActionFeedItem[],
  history: ActionHistoryItem[],
  first: boolean,
) {
  if (!action) return;

  if (!first)
    future.push({
      actionId: action.id,
      moduleId: action.moduleId,
      visible: !action.sceneId,
      sessionAttemptId: sessionAttemptId,
      actionRegisterId: null,
    });

  const next = processModOut(entity, action.outs[Handles.DEFAULT]);
  if (next && next.status > -1 && next.id > 0)
    addActionToFuture(sessionAttemptId, entity, next, future, history, false);
  else if (action.childId && action.childId !== action.id && !future.some(f => f.actionId === action.childId)) {
    const child = parseActionDAO(entity, action.childId);
    if (child && child.status > -1 && child.id > 0)
      return addActionToFuture(sessionAttemptId, entity, child, future, history, false);
  }
}

function nextModule(
  sessionAttemptId: number,
  entity: IEntityReducer,
  moduleId: number,
  history: ActionHistoryItem[],
): IActionFeedItem[] {
  const module = entity[EntityTypes.MODULE][moduleId] as IModuleAssign;

  if (!module) return null;

  if (![ModuleType.STANDARD].includes(module.type)) {
    return [
      {
        moduleId: module.id,
        sessionAttemptId: module.sessionAttemptId || sessionAttemptId,
        actionRegisterId: null,
      },
    ];
  }

  let future = [];

  if (history.length) {
    future = history.map(historyItem => {
      return {
        ...historyItem,
        moduleId: module.id,
        sessionAttemptId: sessionAttemptId,
        actionRegisterId: historyItem.actionRegisterId ?? null,
      };
    });

    addActionToFuture(
      sessionAttemptId,
      entity,
      parseActionDAO(entity, future[future.length - 1].actionId),
      future,
      history,
      true,
    );
  } else if (module.actionId)
    addActionToFuture(sessionAttemptId, entity, parseActionDAO(entity, module.actionId), future, history, false);
  else {
    const actions = getEntities(entity, EntityTypes.ACTION, 'moduleId', module.id).map(a => parseAction(entity, a.id));

    const action = getFirstAction(actions);

    if (action) addActionToFuture(sessionAttemptId, entity, parseActionDAO(entity, action.id), future, history, false);
  }

  return future;
}
